import React from 'react';

const LeftSidebar = ({
  activeSection,
  onSectionChange,
  isCollapsed,
  onToggleCollapse,
  darkMode,
  user = null,
  onOpenUserPanel
}) => {
  const menuItems = [
    {
      id: 'overview',
      label: 'Dashboard',
      shortLabel: 'Dashboard',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
        </svg>
      )
    },
    {
      id: 'upload',
      label: 'Data Sources',
      shortLabel: 'Data',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      id: 'history',
      label: 'Upload History',
      shortLabel: 'History',
      icon: (
        <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
        </svg>
      )
    }
  ];

  const isActive = (item) => activeSection === item.id;
  const userInitial = (user?.name || user?.email || 'U').charAt(0).toUpperCase();
  const userRole = user?.role || 'User';

  return (
    <div
      className={`${isCollapsed ? 'w-20' : 'w-56'} transition-all duration-300 ease-in-out ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-r flex flex-col h-full relative flex-shrink-0`}
    >
      {/* Logo */}
      <div className={`p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'} flex justify-center`}>
        <img src="/leftpanel.png" alt="American Green Solutions" className="h-10 w-auto object-contain" />
      </div>

      {/* Collapse button */}
      <button
        type="button"
        onClick={onToggleCollapse}
        className={`absolute -right-3 top-16 w-6 h-6 rounded-full flex items-center justify-center shadow z-10 ${darkMode ? 'bg-gray-700 border border-gray-600 text-gray-300' : 'bg-white border border-gray-200 text-gray-500'} hover:opacity-80`}
      >
        <svg className={`w-4 h-4 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Nav items */}
      <nav className="flex-1 py-1 overflow-y-auto overflow-x-hidden">
        {menuItems.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onSectionChange(item.id)}
            className={`w-full flex ${isCollapsed ? 'flex-col items-center py-2.5 px-2' : 'flex-row items-center px-4 py-2.5 gap-3'} transition-all duration-200 ${
              isActive(item)
                ? 'bg-[#679632]/10 border-l-4 border-[#679632] text-[#679632]'
                : `border-l-4 border-transparent ${darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'}`
            }`}
          >
            <span className={isActive(item) ? 'text-[#679632]' : darkMode ? 'text-gray-400' : 'text-gray-500'}>
              {item.icon}
            </span>
            {!isCollapsed && <span className="text-sm font-medium truncate">{item.label}</span>}
            {isCollapsed && <span className="text-[10px] font-medium text-center leading-tight mt-0.5 truncate w-full">{item.shortLabel}</span>}
          </button>
        ))}
      </nav>

      {/* Bottom action — opens the right-side panel. Shows the user when signed in
          (SSO), otherwise a neutral Settings entry for the no-login apps. */}
      {onOpenUserPanel && (
        <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            type="button"
            onClick={onOpenUserPanel}
            title="Settings"
            className={`w-full flex items-center ${isCollapsed ? 'flex-col justify-center py-2.5 px-2' : 'gap-3 px-4 py-3'} ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}
          >
            {user ? (
              <div className="w-8 h-8 rounded-full bg-[#679632] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                {userInitial}
              </div>
            ) : (
              <span className={isCollapsed ? '' : 'flex-shrink-0'} >
                <svg className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </span>
            )}
            {!isCollapsed && (
              <div className="flex-1 text-left min-w-0">
                <p className={`text-sm font-medium truncate ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{user ? (user.name || 'Account') : 'Settings'}</p>
                {user && <p className="text-xs text-gray-500 truncate">{userRole}</p>}
              </div>
            )}
            {isCollapsed && (
              <span className={`text-[10px] font-medium text-center leading-tight mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{user ? 'Account' : 'Settings'}</span>
            )}
            {!isCollapsed && (
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default LeftSidebar;
